import { useState, useMemo } from 'preact/hooks'
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isToday, isWithinInterval, isSameDay, isBefore, isAfter } from 'date-fns'
import { de } from 'date-fns/locale'
import clsx from 'clsx'

interface DateRangePickerProps {
    selectedDate?: { start: Date | null; end: Date | null }
    onDateSelect: (range: { start: Date | null; end: Date | null }) => void
    minDate?: Date
    maxDate?: Date
    locale?: Locale
    setIsOpen?: (value: boolean) => void
}

export function DateRangePicker({
    selectedDate = { start: null, end: null },
    onDateSelect,
    minDate = new Date(),
    maxDate = addMonths(new Date(), 24),
    locale = de,
    setIsOpen
}: DateRangePickerProps) {
    const [currentMonth, setCurrentMonth] = useState(new Date())
    const [, setIsSelectingRange] = useState(false)
    const [hoverDate, setHoverDate] = useState<Date | null>(null)

    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd })

    const startDayOfWeek = getDay(monthStart)
    const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1

    const emptyDays = Array.from({ length: adjustedStartDay }, (_, i) => i)

    const dayLabels = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO']

    const canGoToPreviousMonth = useMemo(() => {
        const prevMonth = subMonths(currentMonth, 1)
        return !isBefore(prevMonth, startOfMonth(minDate))
    }, [currentMonth, minDate])

    const canGoToNextMonth = useMemo(() => {
        const nextMonth = addMonths(currentMonth, 1)
        return !isAfter(nextMonth, startOfMonth(maxDate))
    }, [currentMonth, maxDate])

    const handlePreviousMonth = () => {
        if (canGoToPreviousMonth) {
            setCurrentMonth(prev => subMonths(prev, 1))
        }
    }

    const handleNextMonth = () => {
        if (canGoToNextMonth) {
            setCurrentMonth(prev => addMonths(prev, 1))
        }
    }

    const handleDateClick = (date: Date) => {
        if (isBefore(date, minDate) || isAfter(date, maxDate)) {
            return
        }

        // Create a new date set to noon to avoid timezone issues
        const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0)

        if (!selectedDate.start || (selectedDate.start && selectedDate.end)) {
            onDateSelect({ start: normalizedDate, end: null })
            setIsSelectingRange(true)
            setHoverDate(null)
        } else if (selectedDate.start && !selectedDate.end) {
            if (isBefore(date, selectedDate.start)) {
                onDateSelect({ start: normalizedDate, end: selectedDate.start })
            } else {
                onDateSelect({ start: selectedDate.start, end: normalizedDate })
            }
            setIsSelectingRange(false)
            setHoverDate(null)
        }
    }

    const handleDateHover = (date: Date) => {
        if (selectedDate.start && !selectedDate.end) {
            setHoverDate(date)
        }
    }

    const getDateStyle = (date: Date) => {
        const isDisabled = isBefore(date, minDate) || isAfter(date, maxDate)
        const isSelected = selectedDate.start && (isSameDay(date, selectedDate.start) || (selectedDate.end && isSameDay(date, selectedDate.end)))
        const isInRange = selectedDate.start && selectedDate.end && isWithinInterval(date, {
            start: selectedDate.start,
            end: selectedDate.end
        })
        const isStartOrEnd = selectedDate.start && selectedDate.end && (isSameDay(date, selectedDate.start) || isSameDay(date, selectedDate.end))

        const isInHoverRange = selectedDate.start && !selectedDate.end && hoverDate &&
            isWithinInterval(date, {
                start: isBefore(hoverDate, selectedDate.start) ? hoverDate : selectedDate.start,
                end: isBefore(hoverDate, selectedDate.start) ? selectedDate.start : hoverDate
            })

        const classes = [
            'w-full h-full flex text-[12px] items-center justify-center text-xs cursor-pointer transition-colors aspect-square',
        ]

        if (isToday(date)) {
            classes.push('font-semibold text-theme-black')
        } else if (isDisabled) {
            classes.push('text-theme-calendar-disabled cursor-not-allowed')
        } else if (isSelected || isStartOrEnd) {
            classes.push('bg-theme-primary text-white font-semibold')
        } else if ((isInRange && !isStartOrEnd) || isInHoverRange) {
            classes.push('bg-theme-hintergrund font-semibold')
        } else {
            classes.push('hover:bg-theme-hintergrund')
        }

        return classes.join(' ')
    }

    return (
        <div className="bg-white pt-4 mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={handlePreviousMonth}
                    disabled={!canGoToPreviousMonth}
                    className={clsx(
                        'p-1 rounded hover:bg-theme-hintergrund transition-colors',
                        !canGoToPreviousMonth && 'text-theme-calendar-disabled cursor-not-allowed hover:bg-transparent'
                    )}
                >
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                </button>
                <div className="text-center">
                    <span className="text-[12px] font-semibold">
                        {format(currentMonth, 'MMMM yyyy', { locale })}
                    </span>
                </div>

                <button
                    onClick={handleNextMonth}
                    disabled={!canGoToNextMonth}
                    className={clsx(
                        'p-1 rounded hover:bg-theme-hintergrund transition-colors',
                        !canGoToNextMonth && 'text-theme-calendar-disabled cursor-not-allowed hover:bg-transparent'
                    )}
                >
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>

            {/* Day labels */}
            <div className="grid grid-cols-7 mb-2">
                {dayLabels.map(day => (
                    <div key={day} className="text-[12px] font-semibold text-gray-600 text-center p-2">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7">
                {/* Empty cells for days before month start */}
                {emptyDays.map(day => (
                    <div key={`empty-${day}`} className="h-8" />
                ))}

                {/* Calendar days */}
                {calendarDays.map(date => (
                    <div
                        key={date.toISOString()}
                        className={getDateStyle(date)}
                        onClick={() => handleDateClick(date)}
                        onMouseEnter={() => handleDateHover(date)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleDateClick(date)
                            }
                        }}
                    >
                        {format(date, 'd')}
                    </div>
                ))}
            </div>

            <div className={`flex justify-between mt-5`}>
                <button className={`btn height-40 ghost red rounded-8px! px-5! font-montserrat! font-bold! border-2! cursor-pointer`}
                        onClick={() => {
                            onDateSelect({ start: null, end: null })
                            if(setIsOpen) {
                                setIsOpen(false)
                            }
                        }}>Löschen</button>
                <button className={`btn height-40 red rounded-8px! px-5! font-montserrat! cursor-pointer`}
                        onClick={() => {
                            if(setIsOpen) {
                                setIsOpen(false)
                            }
                        }}>Weiter</button>
            </div>
        </div>
    )
}
